import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";

const SLUG = "vanhempien-tarinoita";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Vanhempien arjen esimerkkejä — Montessori kotona";
const DESC = "Kolme esimerkkitarinaa lapsiperhearjesta: miten Pikler-kiipeilyteline, oppimistorni ja matalat hyllyt voivat tukea lapsen itsenäisyyttä kotona.";
const PUBLISHED = "2026-06-18";
const HERO = articleHeroFor(SLUG);

export const Route = createFileRoute("/montessori-koulu/vanhempien-tarinoita")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Esimerkkitarinoita"
      title={TITLE}
      lead="Seuraavat tilanteet ovat esimerkkitarinoita tavallisesta lapsiperhearjesta. Ne havainnollistavat, miten Montessori-ajattelua voi soveltaa kotona pienillä ja käytännöllisillä tavoilla."
      readingMinutes={6}
      publishedISO={PUBLISHED}
    >
      <p><em>Huom. Tarinat ovat kuvitteellisia esimerkkejä, eivät asiakaspalautteita.</em></p>

      <h2>Esimerkkitarina 1: kaksivuotias keittiössä</h2>
      <p>Perheen kaksivuotias haluaa mukaan ruoanlaittoon, mutta sylissä kantaminen ei enää onnistu. Oppimistorni nostaa lapsen työtason korkeudelle turvallisesti. Ensimmäisellä viikolla lapsi vain seuraa vierestä, myöhemmin hän sekoittaa taikinaa ja pesee kasviksia. Vanhempi saa keittiöön rauhallisia hetkiä, lapsi kokemuksen osallisuudesta.</p>
      <p>Ikäsuositus vaihtelee tuotteen ja valmistajan mukaan. Tarkista aina tuotekohtainen ikäsuositus ja turvallisuusohjeet tuotteen omalta sivulta ja valvo pienen lapsen käyttöä.</p>

      <h2>Esimerkkitarina 2: pitkä talvi sisätiloissa</h2>
      <p>Kerrostalokodissa talvi tarkoittaa kuukausia sisällä. Kiipeilykolmio ja ramppi antavat karkeamotoriselle liikkeelle oman paikan olohuoneessa: lapsi harjoittelee nousua ja laskeutumista omaan tahtiinsa, palaa haasteen ääreen päivittäin ja purkaa energiaa turvallisesti.</p>
      <p>Kiipeilyvälineitä käytetään pehmeällä alustalla ja aikuisen valvonnassa. Enimmäiskuormitus ja ikäsuositus ilmoitetaan tuotekohtaisesti tuotesivulla.</p>

      <h2>Esimerkkitarina 3: matala hylly ison lelulaatikon tilalle</h2>
      <p>Ison lelulaatikon tilalle vaihdetaan kolme matalaa hyllyä, joilla on kerrallaan noin kahdeksan lelua näkyvillä. Kun jokaisella lelulla on oma paikkansa, siivoamisesta tulee lapselle hahmotettava tehtävä eikä rangaistus — ja valinnanvaikeus vähenee.</p>

      <h2>Yhteinen havainto</h2>
      <p>Näissä esimerkeissä lasta ei <em>opeteta</em> käyttämään uutta kalustetta. Kaluste asetetaan lapsen ulottuville ja aikuinen antaa tilaa. Tämä on Montessori-ajattelun ydin: <strong>oikea ympäristö tukee oppimista.</strong></p>

      <p>Kun saamme asiakkailtamme vahvistettuja kokemuksia, julkaisemme ne erikseen omassa osiossaan &rdquo;Asiakkaidemme kokemuksia&rdquo;. Emme julkaise keksittyjä arvosteluja emmekä arvosanoja.</p>

      <p>Haluatko aloittaa? Tutustu suosituimpiin kalusteisiin: <Link to="/kauppa">Selaa Montessori-mallistoa →</Link></p>
      <p>Lue myös: <Link to="/montessori-koulu/montessori-kalusteet-kotiin">Mistä aloittaa Montessori-kalusteiden kanssa</Link>.</p>
    </BlogPost>
  );
}